import { ToggleGroup, ToggleGroupItem } from '@monksflow/monks-ui';
import { AspectRatioIcon } from './AspectRatioIcon';
import {
  VideoSettings,
  VEO_ASPECT_RATIOS,
  OMNI_DURATIONS,
  OMNI_RESOLUTIONS,
  VeoAspectRatio,
  Resolution,
} from '../VideoGenerationOptions.types';
import { useTranslation } from 'react-i18next';

export type OmniVideoOptions = Extract<VideoSettings, { model: 'gemini-omni-1.1-flash' }>;

interface OmniOptionsProps {
  value: OmniVideoOptions;
  onChange: (value: VideoSettings) => void;
  disabled?: boolean;
}

/**
 * Renders the chat options for Gemini Omni. Unlike Veo, every resolution, ratio and duration
 * combine freely, so there is nothing to normalise.
 */
export const OmniGenerationOptions = ({ value, onChange, disabled }: OmniOptionsProps) => {
  const { t } = useTranslation('chats');

  return (
    <>
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground-subtle">
          {t('chats.omniOptions.aspectRatio')}
        </span>
        <ToggleGroup
          type="single"
          value={value.aspectRatio}
          onValueChange={(next) => {
            if (next) onChange({ ...value, aspectRatio: next as VeoAspectRatio });
          }}
          variant="outline"
          size="sm"
          className="grid grid-cols-2 w-full"
          disabled={disabled}
        >
          {VEO_ASPECT_RATIOS.map((option) => (
            <ToggleGroupItem
              key={option.value}
              value={option.value}
              className="flex flex-col gap-1 h-auto py-2"
            >
              <AspectRatioIcon orientation={option.orientation} />
              <span className="text-xs">{option.label}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground-subtle">
          {t('chats.omniOptions.resolution')}
        </span>
        <ToggleGroup
          type="single"
          value={value.resolution}
          onValueChange={(next) => {
            if (next) onChange({ ...value, resolution: next as Resolution });
          }}
          variant="outline"
          size="sm"
          className="grid grid-cols-2 w-full"
          disabled={disabled}
        >
          {OMNI_RESOLUTIONS.map((resolution) => (
            <ToggleGroupItem key={resolution} value={resolution} className="py-2">
              <span className="text-xs">{resolution}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground-subtle">
          {t('chats.omniOptions.duration')}
        </span>
        <ToggleGroup
          type="single"
          value={String(value.durationSeconds)}
          onValueChange={(next) => {
            if (next) onChange({ ...value, durationSeconds: Number(next) });
          }}
          variant="outline"
          size="sm"
          className="grid grid-cols-4 w-full"
          disabled={disabled}
        >
          {OMNI_DURATIONS.map((option) => (
            <ToggleGroupItem key={option.value} value={String(option.value)} className="py-2">
              <span className="text-xs">{option.label}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {value.endFrame && !value.startFrame && (
          <span className="text-xs text-muted-foreground">
            {t('chats.videoGenerationOptions.endFrameNeedsStartFrame')}
          </span>
        )}
      </div>
    </>
  );
};
